import * as React from "react";
import { useParams } from "react-router";
import courseApi from "../../../api/course";

const RANGES = [7, 30];
const BAR_AREA_HEIGHT = 80;

const TeacherPortalStudentActivityWidget = () => {
    const { courseid } = useParams();
    const [days, setDays] = React.useState(7);
    const [data, setData] = React.useState(null);
    const [error, setError] = React.useState("");
    // Each range is fetched once, so toggling back and forth is instant
    const cache = React.useRef({});

    React.useEffect(() => {
        if (cache.current[days]) {
            setData(cache.current[days]);
            setError("");
            return undefined;
        }
        let cancelled = false;
        setError("");
        courseApi
            .getTeacherStudentActivity(courseid, days)
            .then((response) => {
                cache.current[days] = response.data;
                if (!cancelled) setData(response.data);
            })
            .catch(() => {
                if (!cancelled) setError("Failed to load student activity.");
            });
        return () => {
            cancelled = true;
        };
    }, [courseid, days]);

    const maxDaily = data ? Math.max(1, ...data.daily.map((d) => d.students)) : 1;
    const delta = data ? data.active_students - data.prior_active_students : 0;

    return (
        <div className="text-left">
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">Student activity</h2>
                <div role="group" aria-label="Time range" className="flex gap-1">
                    {RANGES.map((range) => (
                        <button
                            key={range}
                            type="button"
                            aria-pressed={range === days}
                            onClick={() => setDays(range)}
                            className={
                                "rounded-full px-3 py-1 text-sm " +
                                (range === days
                                    ? "bg-card_dark font-semibold text-txt_primary"
                                    : "text-icon_alt hover:bg-card")
                            }
                        >
                            {range} days
                        </button>
                    ))}
                </div>
            </div>

            {error && <p className="text-error">{error}</p>}
            {!error && data === null && <p>Loading student activity...</p>}
            {!error && data !== null && (
                <div className="mt-3 flex flex-wrap gap-8">
                    <div className="min-w-[120px]">
                        <div className="text-3xl font-semibold">{data.active_students}</div>
                        <div className="text-sm text-icon_alt">/ {data.total_students}</div>
                        <div className="text-sm">Active students</div>
                        <div
                            className={
                                "text-xs " +
                                (delta > 0 ? "text-green-700" : delta < 0 ? "text-error" : "text-icon_alt")
                            }
                        >
                            {delta > 0 ? "+" : ""}{delta} vs prior period
                        </div>
                    </div>

                    <div className="min-w-[200px] flex-1">
                        <h3 className="font-semibold">Daily activity</h3>
                        <div
                            className={"flex items-end " + (days === 7 ? "gap-2" : "gap-0.5")}
                            style={{ height: BAR_AREA_HEIGHT }}
                        >
                            {data.daily.map((d, i) => (
                                <div
                                    key={d.date}
                                    title={`${d.date}: ${d.students} students`}
                                    className={"flex-1 rounded-sm " + (i === data.daily.length - 1 ? "bg-bar_average" : "bg-bar_score/40")}
                                    style={{ height: Math.max(2, (BAR_AREA_HEIGHT * d.students) / maxDaily) }}
                                />
                            ))}
                        </div>
                        <div className="text-xs text-icon_alt">Students active</div>
                    </div>

                    <div className="min-w-[180px]">
                        <h3 className="font-semibold">Top topics</h3>
                        {data.top_topics.length === 0 && <p className="text-sm">No activity yet</p>}
                        {data.top_topics.map((topic) => (
                            <div key={topic.constructid} className="flex justify-between gap-4 text-sm">
                                <span>{topic.construct_name}</span>
                                <span className="text-icon_alt">{topic.students}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TeacherPortalStudentActivityWidget;
